import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone } from 'three/addons/utils/SkeletonUtils.js';

export type ModelLoader = { loadAsync(url: string): Promise<Pick<GLTF, 'scene' | 'animations'> & Partial<Pick<GLTF, 'scenes'>>> };
export type ModelInstance = {
  root: THREE.Group;
  animations: THREE.AnimationClip[];
  release(): void;
};
type Disposable = { dispose(): void };

/** Owns cached geometry/textures; acquired instances own only their cloned materials. */
export class ModelLibrary {
  private readonly cache = new Map<string, ReturnType<ModelLoader['loadAsync']>>();
  private readonly shared = new Set<Disposable>();
  private readonly bitmaps = new Set<ImageBitmap>();
  private readonly closedBitmaps = new WeakSet<ImageBitmap>();
  private references = 0;
  private disposed = false;

  constructor(private readonly loader: ModelLoader = new GLTFLoader()) {}

  async acquire(url: string): Promise<ModelInstance> {
    this.assertOpen();
    let pending = this.cache.get(url);
    if (!pending) {
      // Defer invocation so synchronous loader errors also evict the cached promise.
      pending = Promise.resolve().then(() => this.loader.loadAsync(url)).then((model) => {
        for (const scene of new Set([model.scene, ...(model.scenes ?? [])])) {
          scene.traverse((object) => {
            const mesh = object as THREE.Mesh;
            if (mesh.geometry) this.shared.add(mesh.geometry);
            for (const material of this.materials(mesh)) {
              this.shared.add(material);
              this.collectTextures(material);
            }
            if ((object as THREE.SkinnedMesh).isSkinnedMesh) {
              this.shared.add((object as THREE.SkinnedMesh).skeleton);
            }
          });
        }
        this.flush();
        return model;
      }).catch((error: unknown) => {
        this.cache.delete(url);
        throw error;
      });
      this.cache.set(url, pending);
    }
    const model = await pending;
    this.assertOpen();
    const root = clone(model.scene) as THREE.Group;
    const materials = new Map<THREE.Material, THREE.Material>();
    const skeletons = new Set<THREE.Skeleton>();
    const copyMaterial = (source: THREE.Material) => {
      let material = materials.get(source);
      if (!material) {
        material = source.clone();
        materials.set(source, material);
      }
      return material;
    };
    const cleanup = () => {
      for (const material of materials.values()) material.dispose();
      for (const skeleton of skeletons) skeleton.dispose();
    };
    let animations: THREE.AnimationClip[];
    try {
      root.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if ((object as THREE.SkinnedMesh).isSkinnedMesh) skeletons.add((object as THREE.SkinnedMesh).skeleton);
        if (mesh.material) mesh.material = Array.isArray(mesh.material)
          ? mesh.material.map(copyMaterial) : copyMaterial(mesh.material);
      });
      animations = model.animations.map((clip) => clip.clone());
    } catch (error) {
      cleanup();
      throw error;
    }
    this.references++;
    let released = false;
    return { root, animations, release: () => {
      if (released) return;
      released = true;
      cleanup();
      this.references--;
      this.flush();
    } };
  }

  dispose(): void {
    this.disposed = true;
    this.cache.clear();
    this.flush();
  }

  private assertOpen(): void {
    if (this.disposed) throw new Error('ModelLibrary is disposed');
  }

  private materials(mesh: THREE.Mesh): THREE.Material[] {
    return mesh.material ? (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) : [];
  }

  private collectTextures(material: THREE.Material): void {
    const seen = new Set<object>();
    const visit = (value: unknown): void => {
      if (!value || typeof value !== 'object' || seen.has(value)) return;
      seen.add(value);
      if ((value as THREE.Texture).isTexture) {
        const texture = value as THREE.Texture;
        this.shared.add(texture);
        const data: unknown = texture.source.data;
        if (typeof ImageBitmap !== 'undefined' && data instanceof ImageBitmap && !this.closedBitmaps.has(data)) {
          this.bitmaps.add(data);
        }
      } else for (const child of Object.values(value)) visit(child);
    };
    // Includes texture slots and ShaderMaterial uniform structures.
    for (const [key, value] of Object.entries(material)) {
      if (key !== 'userData') visit(value);
    }
  }

  private flush(): void {
    if (!this.disposed || this.references !== 0) return;
    for (const resource of this.shared) resource.dispose();
    this.shared.clear();
    for (const bitmap of this.bitmaps) {
      this.closedBitmaps.add(bitmap);
      bitmap.close();
    }
    this.bitmaps.clear();
  }
}
